'use client';

import { cn } from '@/lib/utils';
import type { ProgramTemplateProviderId } from '@court-sync/shared';
import { ProgramTemplateLogoPlaceholder } from './programTemplateLogoPlaceholders';
import { PenLine } from 'lucide-react';

type ProviderSelection = ProgramTemplateProviderId | 'custom';

export function ProgramTemplateProviderCard({
  selection,
  selected,
  title,
  description,
  regionLabel,
  sportLabel,
  onSelect,
}: {
  selection: ProviderSelection;
  selected: boolean;
  title: string;
  description: string;
  regionLabel?: string;
  sportLabel?: string;
  onSelect: () => void;
}) {
  const isCustom = selection === 'custom';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex w-full flex-col items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors',
        selected
          ? 'border-indigo-500 bg-indigo-50/80 dark:border-indigo-400 dark:bg-indigo-950/40'
          : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600'
      )}
      aria-pressed={selected}
    >
      <div className="flex w-full items-start gap-3">
        {isCustom ? (
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-800">
            <PenLine className="h-7 w-7 text-indigo-500 dark:text-indigo-400" aria-hidden />
          </div>
        ) : (
          <ProgramTemplateLogoPlaceholder providerId={selection} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold text-gray-900 dark:text-white">{title}</p>
            {sportLabel ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {sportLabel}
              </span>
            ) : null}
          </div>
          {regionLabel ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{regionLabel}</p>
          ) : null}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-3">{description}</p>
        </div>
      </div>
    </button>
  );
}
